// src/api/groupChatApi.ts

import conveAxios from './conveApi';
import {
  GroupRoom,
  GroupMessage,
  GroupMember,
  UserSearchResult,
} from '../types/groupChat';

export const groupChatApi = {
  // 그룹 채팅방 생성
  createGroupRoom: async (data: {
    title: string;
    memberIds: number[];
    aiEnabled: boolean;
  }): Promise<GroupRoom> => {
    try {
      const res = await conveAxios.post('/group/rooms', data);
      return res.data.data as GroupRoom;
    } catch (e: any) {
      alert('채팅방 생성에 실패했습니다: ' + (e?.response?.data?.message ?? e?.message));
      throw e;
    }
  },

  // 1:1 채팅방 생성
  createDirectRoom: async (targetUserId: number): Promise<GroupRoom> => {
    try {
      const res = await conveAxios.post('/group/rooms/direct', { targetUserId });
      return res.data.data as GroupRoom;
    } catch (e: any) {
      alert('채팅방 생성에 실패했습니다: ' + (e?.response?.data?.message ?? e?.message));
      throw e;
    }
  },

  // 내 채팅방 목록
  getMyRooms: async (): Promise<GroupRoom[]> => {
    try {
      const res = await conveAxios.get('/group/rooms');
      return res.data.data as GroupRoom[];
    } catch (e: any) {
      alert('채팅방 목록 조회에 실패했습니다: ' + (e?.response?.data?.message ?? e?.message));
      throw e;
    }
  },

  // 채팅방 상세 조회
  getRoomDetail: async (roomId: number): Promise<GroupRoom> => {
    try {
      const res = await conveAxios.get(`/group/rooms/${roomId}`);
      return res.data.data as GroupRoom;
    } catch (e: any) {
      alert('채팅방 조회에 실패했습니다: ' + (e?.response?.data?.message ?? e?.message));
      throw e;
    }
  },

  // 채팅방 삭제
  deleteRoom: async (roomId: number): Promise<void> => {
    try {
      await conveAxios.delete(`/group/rooms/${roomId}`);
    } catch (e: any) {
      alert('채팅방 삭제에 실패했습니다: ' + (e?.response?.data?.message ?? e?.message));
      throw e;
    }
  },

  // AI 토글
  toggleAi: async (roomId: number, aiEnabled: boolean): Promise<void> => {
    try {
      await conveAxios.put(`/group/rooms/${roomId}/ai`, { aiEnabled });
    } catch (e: any) {
      alert('AI 설정 변경에 실패했습니다: ' + (e?.response?.data?.message ?? e?.message));
      throw e;
    }
  },

  // 멤버 목록 조회
  getMembers: async (roomId: number): Promise<GroupMember[]> => {
    try {
      const res = await conveAxios.get(`/group/rooms/${roomId}/members`);
      return res.data.data as GroupMember[];
    } catch (e: any) {
      alert('멤버 목록 조회에 실패했습니다: ' + (e?.response?.data?.message ?? e?.message));
      throw e;
    }
  },

  // 멤버 초대
  inviteMembers: async (roomId: number, userIds: number[]): Promise<void> => {
    try {
      await conveAxios.post(`/group/rooms/${roomId}/members/invite`, { userIds });
    } catch (e: any) {
      alert('멤버 초대에 실패했습니다: ' + (e?.response?.data?.message ?? e?.message));
      throw e;
    }
  },

  // 멤버 퇴장/강퇴
  removeMember: async (roomId: number, userId: number): Promise<void> => {
    try {
      await conveAxios.delete(`/group/rooms/${roomId}/members/${userId}`);
    } catch (e: any) {
      alert('멤버 제거에 실패했습니다: ' + (e?.response?.data?.message ?? e?.message));
      throw e;
    }
  },

  // 메시지 목록 조회 (커서 기반 페이징)
  // lastMessageId=null이면 최신부터, 숫자면 해당 id 이전 메시지 로드
  // 서버는 DESC 순으로 반환 → 프론트에서 reverse() 필요
  getMessages: async (roomId: number, lastMessageId: number | null, size: number): Promise<GroupMessage[]> => {
    try {
      const params: Record<string, unknown> = { size };
      if (lastMessageId != null) params.lastMessageId = lastMessageId;
      const res = await conveAxios.get(`/group/rooms/${roomId}/messages`, { params });
      return res.data.data as GroupMessage[];
    } catch (e: any) {
      alert('메시지 조회에 실패했습니다: ' + (e?.response?.data?.message ?? e?.message));
      throw e;
    }
  },

  // 메시지 전송 (REST fallback)
  sendMessage: async (
    roomId: number,
    content: string,
    mentionAi: boolean
  ): Promise<GroupMessage> => {
    try {
      const res = await conveAxios.post(`/group/rooms/${roomId}/messages`, {
        content,
        mentionAi,
      });
      return res.data.data as GroupMessage;
    } catch (e: any) {
      alert('메시지 전송에 실패했습니다: ' + (e?.response?.data?.message ?? e?.message));
      throw e;
    }
  },

  // 읽음 표시 업데이트
  updateRead: async (roomId: number, lastReadMsgId: number): Promise<void> => {
    try {
      await conveAxios.post(`/group/rooms/${roomId}/read`, { lastReadMsgId });
    } catch (e: any) {
      // 읽음 표시는 실패해도 alert 생략
      console.error('읽음 표시 업데이트 실패:', e);
    }
  },

  // 사용자 검색
  searchUsers: async (query: string): Promise<UserSearchResult[]> => {
    try {
      const res = await conveAxios.get('/users/search', { params: { q: query } });
      return res.data.data as UserSearchResult[];
    } catch (e: any) {
      alert('사용자 검색에 실패했습니다: ' + (e?.response?.data?.message ?? e?.message));
      throw e;
    }
  },
};
